import { ResponseError } from '@backstage/errors';
import {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { FfgApi } from './api';
import {
  FFGEnvironment,
  UnusedRoutesResponse,
  FfgSensitiveRoutesResponse,
  GatewayServicesResponse,
} from '@mercedes-benz/backstage-plugin-ffg-common';

/**
 * Options for creating a FfgClient client.
 *
 * @public
 */
export interface FfgClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  identityApi: IdentityApi;
}

/**
 * An implementation of the FFG API client
 *
 * @public
 */
export class FfgClient implements FfgApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly identityApi: IdentityApi;

  constructor(options: FfgClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.identityApi = options.identityApi;
  }

  async getUnusedRoutes(
    environment: FFGEnvironment,
  ): Promise<UnusedRoutesResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ffg');
    const { token } = await this.identityApi.getCredentials();
    const targetUrl = `${baseUrl}/unused-routes?environment=${encodeURIComponent(environment)}`;

    const res = await this.fetchApi.fetch(targetUrl, {
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        : {
            'Content-Type': 'application/json',
          },
    });

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }

    return (await res.json()) as UnusedRoutesResponse;
  }

  async getSensitiveRoutes(
    environment: FFGEnvironment,
  ): Promise<FfgSensitiveRoutesResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ffg');
    const { token } = await this.identityApi.getCredentials();
    const targetUrl = `${baseUrl}/sensitive-routes?environment=${encodeURIComponent(environment)}`;

    const res = await this.fetchApi.fetch(targetUrl, {
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        : {
            'Content-Type': 'application/json',
          },
    });

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }

    return (await res.json()) as FfgSensitiveRoutesResponse;
  }

  async getGatewayServices(
    environment: FFGEnvironment,
  ): Promise<GatewayServicesResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ffg');
    const { token } = await this.identityApi.getCredentials();
    const targetUrl = `${baseUrl}/gateway-services?environment=${encodeURIComponent(environment)}`;

    const res = await this.fetchApi.fetch(targetUrl, {
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        : {
            'Content-Type': 'application/json',
          },
    });

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }

    return (await res.json()) as GatewayServicesResponse;
  }
}
